import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { UserType } from '@app/models/user-types.enum';
import { LocalStorageService } from '@app/services/local-storage.service';
import { BaseApiService } from '@app/services/base-api.service';
import { UserService } from '@app/services/user.service';
import { CookieStorageService } from '@app/services/cookie.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseApiService {
  protected override baseUrl: string = environment.API_ENDPOINT + '/auth';

  private tokenKey = 'auth_token'; // Key to store the token in localStorage
  private userRoleKey = 'user_role'; // Key to store user role (Supplier/Vendor)
  private rfqFormDataKey = 'rfqFormData'; // Key to store Supplier RFQ Form Data

  constructor(
    protected override http: HttpClient,
    private userService: UserService,
    private localStorageService: LocalStorageService,
    private readonly cookieStorageService: CookieStorageService,
  ) {
    super(http);
  }

  // SSO Login
  ssoLogin(token: string): Observable<any> {
    return this.post(`/sso`, { token: token }).pipe(
      map((response) => {
        if (response.status === 'Success') {
          return response.data;
        } else {
          console.log('error here : ' + response.message);
          throw new Error(response.message);
        }
      }),
    );
  }

  // Login function
  login(email: string, password: string): Observable<any> {
    // Making POST request to Laravel login API
    return this.post('/login', { email, password }).pipe(
      map((response) => {
        if (response.status === 'Success') {
          return response.data; // Extract 'data' from response
        } else {
          throw new Error(response.message);
        }
      }),
    );
  }

  // Store token and user role in localStorage
  setSession(token: string, role: string, email: string, id: number): void {
    this.cookieStorageService.setItem(this.tokenKey, token);
    this.cookieStorageService.setItem(this.userRoleKey, role);

    // Store user details in UserService
    this.userService.setUserDetails({ id: id, email: email });
  }

  // Logout and remove the token from localStorage
  logout(): void {
    this.cookieStorageService.removeItem(this.tokenKey);
    this.cookieStorageService.removeItem(this.userRoleKey);
    this.userService.clearUserDetails();
    // this.localStorageService.removeItem(this.rfqFormDataKey);
  }

  // Get the current authentication token
  getToken(): string | null {
    return this.cookieStorageService.getItem(this.tokenKey);
  }

  // Check if the user is authenticated
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // Get the current user role (Supplier/Vendor)
  getUserRole(): UserType {
    const role = this.cookieStorageService.getItem(this.userRoleKey);
    return role as UserType;
  }

  // Add a refresh token mechanism if needed in the future
  // refreshToken(): Observable<any> {
  //   const url = `${this.baseUrl}/auth/refresh-token`;
  //   return this.http.post(url, {});
  // }
}
